import React from 'react';
import PropTypes from 'prop-types';
import {
  Avatar,
  Box,
  Card,
  Stack,
  Typography,
  alpha,
  useTheme
} from '@mui/material';

const AnalyticsStatCard = ({
  title,
  value,
  icon,
  color,
  metaItems,
  helperText,
  onClick,
  compact
}) => {
  const theme = useTheme();
  const isClickable = typeof onClick === 'function';
  const hasMetaItems = metaItems.length > 0;
  let resolvedHelperTextMarginTop = 0;

  if (hasMetaItems) {
    resolvedHelperTextMarginTop = compact ? 0.45 : 0.75;
  }

  return (
    <Card
      onClick={onClick}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : -1}
      onKeyDown={(event) => {
        if (!isClickable) {
          return;
        }

        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick();
        }
      }}
      sx={{
        p: compact ? 1.1 : 1.5,
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        border: '1px solid',
        borderColor: alpha(theme.palette.divider, 0.1),
        boxShadow: '0 2px 10px 0 rgba(0,0,0,0.03)',
        transition: 'all 0.2s ease',
        cursor: isClickable ? 'pointer' : 'default',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 4px 16px 0 ${alpha(color, 0.15)}`
        }
      }}
    >
      <Avatar
        variant="rounded"
        sx={{
          bgcolor: alpha(color, 0.1),
          color,
          width: compact ? 38 : 48,
          height: compact ? 38 : 48,
          mr: compact ? 1.1 : 1.5
        }}
      >
        {icon}
      </Avatar>

      <Box sx={{ flexGrow: 1, zIndex: 1 }}>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: compact ? 0.35 : 0.5,
            fontSize: compact ? '0.66rem' : undefined
          }}
        >
          {title}
        </Typography>

        <Typography
          variant={compact ? 'subtitle1' : 'h6'}
          sx={{
            fontWeight: 800,
            color: 'text.primary',
            my: compact ? 0.15 : 0.35,
            lineHeight: 1.2
          }}
        >
          {value}
        </Typography>

        {metaItems.length > 0 && (
          <Stack
            direction="row"
            spacing={compact ? 1 : 1.5}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
          >
            {metaItems.map((item, position) => (
              <React.Fragment key={`${item.label}-${item.value}`}>
                {position > 0 && (
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.disabled' }}
                  >
                    |
                  </Typography>
                )}

                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Box
                    sx={{
                      width: compact ? 6 : 7,
                      height: compact ? 6 : 7,
                      borderRadius: '50%',
                      bgcolor: item.color
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: item.color,
                      fontWeight: 600,
                      fontSize: compact ? '0.68rem' : undefined
                    }}
                  >
                    {item.value}
                    {' '}
                    {item.label}
                  </Typography>
                </Stack>
              </React.Fragment>
            ))}
          </Stack>
        )}

        {helperText && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: resolvedHelperTextMarginTop,
              color: 'text.secondary',
              fontWeight: 600,
              fontSize: compact ? '0.68rem' : undefined
            }}
          >
            {helperText}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          position: 'absolute',
          right: -10,
          bottom: -10,
          opacity: 0.05,
          transform: 'rotate(-15deg)',
          pointerEvents: 'none',
          zIndex: 0
        }}
      >
        {React.cloneElement(icon, { sx: { fontSize: compact ? 56 : 72, color } })}
      </Box>
    </Card>
  );
};

AnalyticsStatCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  icon: PropTypes.element.isRequired,
  color: PropTypes.string.isRequired,
  metaItems: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.node.isRequired,
      color: PropTypes.string
    })
  ),
  helperText: PropTypes.string,
  onClick: PropTypes.func,
  compact: PropTypes.bool
};

AnalyticsStatCard.defaultProps = {
  metaItems: [],
  helperText: undefined,
  onClick: undefined,
  compact: false
};

export default AnalyticsStatCard;
